import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import axios from "axios";
import Cropper from "react-easy-crop";
import { toast } from "react-toastify";
import { loaderDecrement, loaderIncrement } from "../services/globalLoader";
import { notifyApartmentLogoChanged } from "../services/apartmentLogo";

/** Sidebar brand area is ~180×40–45px — crop 4:1 and export retina 480×120. */
const LOGO_CROP_ASPECT = 4 / 1;
const LOGO_OUTPUT_WIDTH = 480;
const LOGO_OUTPUT_HEIGHT = 120;

/** Normalize presign response shapes to { uploadUrl, key, contentType }. */
function unwrapPresignPayload(d) {
  if (!d || typeof d !== "object") return null;

  const nested =
    d.upload && typeof d.upload === "object"
      ? d.upload
      : d.data?.upload && typeof d.data.upload === "object"
        ? d.data.upload
        : null;

  if (nested?.uploadUrl && (nested?.key || nested?.s3Key)) {
    return {
      ...d,
      ...nested,
      key: nested.key || nested.s3Key
    };
  }
  if (d.uploadUrl && (d.key || d.s3Key)) {
    return { ...d, key: d.key || d.s3Key };
  }
  if (d.data && typeof d.data === "object" && d.data.uploadUrl) {
    return {
      ...d,
      ...d.data,
      key: d.data.key || d.data.s3Key
    };
  }
  return d;
}

function pickLogoUrl(data) {
  if (!data || typeof data !== "object") return "";
  return String(
    data.logoUrl ||
      data.logoViewUrl ||
      data.imageUrl ||
      data.url ||
      data.image ||
      data.data?.logoUrl ||
      data.data?.logoViewUrl ||
      data.data?.imageUrl ||
      data.data?.url ||
      ""
  ).trim();
}

function pickS3Key(data, fallback) {
  if (!data || typeof data !== "object") return fallback || "";
  return String(
    data.s3Key || data.key || data.data?.s3Key || data.data?.key || fallback || ""
  ).trim();
}

const LOGO_ALREADY_EXISTS_MSG =
  "Logo Already exist. Delete it before creating a new one";

function getCreateLogoErrorMessage(apiMessage) {
  const msg = String(apiMessage || "").toLowerCase();
  if (
    msg.includes("active apartment logo already exists") ||
    msg.includes("already exists") ||
    msg.includes("soft-delete")
  ) {
    return LOGO_ALREADY_EXISTS_MSG;
  }
  return apiMessage || "Failed to create apartment logo";
}

function createImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (err) => reject(err));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });
}

async function getCroppedLogoFile(imageSrc, pixelCrop, fileName = "logo.png") {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = LOGO_OUTPUT_WIDTH;
  canvas.height = LOGO_OUTPUT_HEIGHT;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not crop image");

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    LOGO_OUTPUT_WIDTH,
    LOGO_OUTPUT_HEIGHT
  );

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Could not export cropped image"))),
      "image/png",
      0.95
    );
  });

  const base = String(fileName || "logo").replace(/\.[^.]+$/, "") || "logo";
  return new File([blob], `${base}-cropped.png`, { type: "image/png" });
}

const LogoCreate = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const imageInputRef = useRef(null);
  const previewObjectUrlRef = useRef(null);
  const cropObjectUrlRef = useRef(null);

  const userId = useMemo(() => {
    try {
      return String(localStorage.getItem("userid") || "").trim();
    } catch {
      return "";
    }
  }, []);

  const [status, setStatus] = useState("active");
  const [logoUrl, setLogoUrl] = useState("");
  const [logoPreview, setLogoPreview] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const [cropOpen, setCropOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState("");
  const [cropSourceName, setCropSourceName] = useState("logo.png");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [cropping, setCropping] = useState(false);

  const clearLocalPreview = useCallback(() => {
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = null;
    }
  }, []);

  const clearCropSrc = useCallback(() => {
    if (cropObjectUrlRef.current) {
      URL.revokeObjectURL(cropObjectUrlRef.current);
      cropObjectUrlRef.current = null;
    }
    setCropSrc("");
    setCroppedAreaPixels(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  }, []);

  useEffect(
    () => () => {
      clearLocalPreview();
      clearCropSrc();
    },
    [clearLocalPreview, clearCropSrc]
  );

  /**
   * 1) appartmentLogo.getUploadUrl
   * 2) PUT binary to uploadUrl
   * 3) appartmentLogo.confirm → WebP logoUrl (save:false; create is separate)
   */
  const uploadAndConfirmLogo = useCallback(
    async (file) => {
      if (!API_BASE_URL) {
        throw new Error("Missing REACT_APP_API_URL in environment");
      }
      if (!userId) {
        throw new Error("Missing userId. Please login again.");
      }
      if (!file) {
        throw new Error("Please choose a logo image.");
      }

      const fileName = file.name || "logo.png";
      const fileContentType =
        file.type && file.type.startsWith("image/") ? file.type : "image/png";

      const presignRes = await axios.post(`${API_BASE_URL}`, {
        action: "appartmentLogo.getUploadUrl",
        userId,
        fileName,
        contentType: fileContentType,
        expiresIn: 900
      });
      const presignRaw = presignRes?.data;
      const okPresign =
        presignRaw?.status === "success" ||
        presignRaw?.success === true ||
        !!presignRaw?.uploadUrl ||
        !!presignRaw?.upload?.uploadUrl ||
        !!presignRaw?.data?.uploadUrl;

      if (!okPresign) {
        throw new Error(presignRaw?.message || "Could not get upload URL");
      }

      const presign = unwrapPresignPayload(presignRaw);
      const uploadUrl = presign?.uploadUrl;
      const s3KeyFromPresign = pickS3Key(presign);
      if (!uploadUrl || !s3KeyFromPresign) {
        throw new Error("Invalid upload URL response (missing uploadUrl/key).");
      }

      const contentTypeForPut =
        presign.contentType || fileContentType || "image/png";

      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": contentTypeForPut },
        body: file
      });

      if (!putRes.ok) {
        const errText = await putRes.text().catch(() => "");
        throw new Error(
          `S3 upload failed (${putRes.status}). ${
            errText ? errText.slice(0, 200) : "Check bucket CORS and Content-Type."
          }`
        );
      }

      const confirmRes = await axios.post(`${API_BASE_URL}`, {
        action: "appartmentLogo.confirm",
        userId,
        s3Key: s3KeyFromPresign,
        save: false,
        expiresIn: 3600
      });
      const confirmData = confirmRes?.data;
      const okConfirm =
        confirmData?.status === "success" || confirmData?.success === true;

      if (!okConfirm) {
        throw new Error(confirmData?.message || "Could not confirm logo upload");
      }

      const confirmedUrl = pickLogoUrl(confirmData);
      if (!confirmedUrl) {
        throw new Error("Confirm succeeded but no logoUrl was returned");
      }

      return {
        logoUrl: confirmedUrl,
        s3Key: pickS3Key(confirmData, s3KeyFromPresign)
      };
    },
    [API_BASE_URL, userId]
  );

  const uploadCroppedFile = async (file) => {
    if (!file) return;

    clearLocalPreview();
    setLogoFile(null);
    setLogoUrl("");
    setLogoPreview("");

    const localUrl = URL.createObjectURL(file);
    previewObjectUrlRef.current = localUrl;
    setLogoFile(file);
    setLogoPreview(localUrl);

    setUploading(true);
    let didIncrement = false;
    try {
      loaderIncrement();
      didIncrement = true;
      const { logoUrl: uploadedUrl } = await uploadAndConfirmLogo(file);
      clearLocalPreview();
      setLogoPreview(uploadedUrl);
      setLogoUrl(uploadedUrl);
      setLogoFile(null);
      toast.success("Logo uploaded");
    } catch (error) {
      toast.error(error?.message || "Logo upload failed");
      clearLocalPreview();
      setLogoFile(null);
      setLogoUrl("");
      setLogoPreview("");
      if (imageInputRef.current) imageInputRef.current.value = "";
    } finally {
      if (didIncrement) loaderDecrement();
      setUploading(false);
    }
  };

  const openCropForFile = (file) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (png, jpg, webp, gif).");
      if (imageInputRef.current) imageInputRef.current.value = "";
      return;
    }
    const maxBytes = 8 * 1024 * 1024;
    if (file.size > maxBytes) {
      toast.error("File is too large (max 8 MB).");
      if (imageInputRef.current) imageInputRef.current.value = "";
      return;
    }
    if (!userId) {
      toast.error("Missing userId. Please login again.");
      if (imageInputRef.current) imageInputRef.current.value = "";
      return;
    }

    clearCropSrc();
    const url = URL.createObjectURL(file);
    cropObjectUrlRef.current = url;
    setCropSourceName(file.name || "logo.png");
    setCropSrc(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setCropOpen(true);
  };

  const closeCropModal = () => {
    setCropOpen(false);
    clearCropSrc();
    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  const onCropComplete = useCallback((_area, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const applyCropAndUpload = async () => {
    if (!cropSrc || !croppedAreaPixels) {
      toast.error("Adjust the crop area first.");
      return;
    }
    setCropping(true);
    try {
      const croppedFile = await getCroppedLogoFile(
        cropSrc,
        croppedAreaPixels,
        cropSourceName
      );
      setCropOpen(false);
      clearCropSrc();
      await uploadCroppedFile(croppedFile);
    } catch (error) {
      toast.error(error?.message || "Could not crop logo");
    } finally {
      setCropping(false);
    }
  };

  const onImageFileChange = (e) => {
    openCropForFile(e.target.files?.[0]);
  };

  const onDropImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (uploading || submitting || cropOpen) return;
    openCropForFile(e.dataTransfer?.files?.[0]);
  };

  const clearLogo = () => {
    if (uploading || submitting) return;
    clearLocalPreview();
    setLogoFile(null);
    setLogoUrl("");
    setLogoPreview("");
    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  const openPicker = () => {
    if (!uploading && !submitting && !cropOpen) imageInputRef.current?.click();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!API_BASE_URL) {
      toast.error("Missing REACT_APP_API_URL in environment");
      return;
    }
    if (!userId) {
      toast.error("Missing userId. Please login again.");
      return;
    }

    setSubmitting(true);
    let didIncrement = false;
    try {
      loaderIncrement();
      didIncrement = true;

      let finalLogoUrl = String(logoUrl || "").trim();

      if (logoFile && !finalLogoUrl) {
        const uploaded = await uploadAndConfirmLogo(logoFile);
        finalLogoUrl = uploaded.logoUrl;
        setLogoUrl(finalLogoUrl);
        clearLocalPreview();
        setLogoPreview(finalLogoUrl);
        setLogoFile(null);
      }

      if (!finalLogoUrl) {
        toast.error("Please upload a logo image first.");
        return;
      }

      const response = await axios.post(`${API_BASE_URL}`, {
        action: "appartmentLogo.create",
        userId,
        logoUrl: finalLogoUrl,
        status: status || "active",
        expiresIn: 3600
      });
      const data = response?.data;

      if (data?.status === "success" || data?.success === true) {
        toast.success(data?.message || "Apartment logo created successfully");
        notifyApartmentLogoChanged();
        navigate("/logo");
      } else {
        toast.error(getCreateLogoErrorMessage(data?.message));
      }
    } catch (error) {
      toast.error(
        getCreateLogoErrorMessage(
          error?.response?.data?.message || error?.message
        )
      );
    } finally {
      if (didIncrement) loaderDecrement();
      setSubmitting(false);
    }
  };

  return (
    <div className="employee-form community-create">
      <div className="content-header">
        <div className="container-fluid community-create-header">
          <div>
            <nav className="community-create-breadcrumb">
              <NavLink to="/logo">Logo</NavLink>
              <span>/</span>
              <span>Add logo</span>
            </nav>
            <h1 className="m-0 txt_lft">Add Apartment Logo</h1>
            <p className="community-create-sub">
              Crop to sidebar size (4:1), then save with status.
            </p>
          </div>
          <NavLink to="/logo" className="btn btn-secondary">
            <i className="fa-solid fa-arrow-left mr-1" /> Back
          </NavLink>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <form onSubmit={handleSubmit} autoComplete="off">
            <div className="row community-create-layout">
              <div className="col-12 col-lg-5 order-2 order-lg-1">
                <div className="community-create-photo-card">
                  <div className="community-create-photo-glow" aria-hidden="true" />
                  <div
                    className={`community-create-dropzone${dragOver ? " is-dragover" : ""}${
                      uploading ? " is-uploading" : ""
                    }${logoPreview ? " has-image" : ""}`}
                    style={{
                      width: "100%",
                      maxWidth: 280,
                      height: 70,
                      borderRadius: 12,
                      margin: "8px auto"
                    }}
                    onClick={openPicker}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openPicker();
                      }
                    }}
                    onDragEnter={(e) => {
                      e.preventDefault();
                      setDragOver(true);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOver(true);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      setDragOver(false);
                    }}
                    onDrop={onDropImage}
                    role="button"
                    tabIndex={0}
                    aria-label="Upload apartment logo"
                  >
                    <input
                      ref={imageInputRef}
                      type="file"
                      className="d-none"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={onImageFileChange}
                      disabled={uploading || submitting || cropOpen}
                    />

                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        className="community-create-avatar-img"
                        style={{ borderRadius: 8, objectFit: "contain" }}
                        onError={() => {
                          if (!logoUrl) setLogoPreview("");
                        }}
                      />
                    ) : (
                      <div
                        className="d-flex align-items-center justify-content-center text-muted"
                        style={{ fontSize: 12, gap: 6 }}
                      >
                        <i className="fa-solid fa-image" />
                        <span>Sidebar logo</span>
                      </div>
                    )}

                    <div className="community-create-drop-hint">
                      {uploading ? (
                        <>
                          <i className="fa-solid fa-spinner fa-spin" /> Uploading…
                        </>
                      ) : logoUrl ? (
                        <>
                          <i className="fa-solid fa-check" /> Click to change
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-crop" /> Drop or click to crop
                        </>
                      )}
                    </div>
                  </div>

                  <p className="community-create-photo-note">
                    Crop size 4:1 (480×120) · fits sidebar brand · max 8 MB
                  </p>

                  <div className="d-flex flex-column" style={{ gap: 8 }}>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary btn-block"
                      onClick={openPicker}
                      disabled={uploading || submitting || cropOpen}
                    >
                      <i className="fa-solid fa-crop mr-1" />{" "}
                      {uploading
                        ? "Uploading…"
                        : logoPreview
                          ? "Change & crop logo"
                          : "Choose & crop logo"}
                    </button>
                    {logoPreview ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary btn-block"
                        onClick={clearLogo}
                        disabled={uploading || submitting}
                      >
                        <i className="fa-solid fa-trash-can mr-1" /> Remove logo
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="col-12 col-lg-7 order-1 order-lg-2">
                <div className="community-create-main-card">
                  <section className="community-create-section last">
                    <div className="community-create-section-head">
                      <span className="section-icon">
                        <i className="fa-solid fa-building" />
                      </span>
                      <div>
                        <h2>Logo details</h2>
                        <p>Set status after cropping and uploading</p>
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Status <span className="required-star">*</span>
                      </label>
                      <select
                        className="form-control"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        disabled={uploading || submitting}
                        required
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>

                    {logoUrl ? (
                      <div className="form-group mb-0">
                        <label className="form-label text-muted">Logo URL</label>
                        <input
                          type="text"
                          className="form-control"
                          value={logoUrl}
                          readOnly
                          tabIndex={-1}
                          style={{
                            backgroundColor: "#f1f3f5",
                            color: "#868e96",
                            fontSize: 12
                          }}
                        />
                      </div>
                    ) : (
                      <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                        Choose an image, crop to 4:1, then continue.
                      </p>
                    )}
                  </section>

                  <div className="community-create-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={uploading || submitting}
                      onClick={() => navigate("/logo")}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary community-create-submit"
                      disabled={uploading || submitting || !logoUrl}
                    >
                      {submitting ? "Saving…" : "Create logo"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {cropOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 2100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16
          }}
          onClick={closeCropModal}
        >
          <div
            className="card"
            style={{ width: "min(640px, 96vw)", maxHeight: "92vh", overflow: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="card-header d-flex justify-content-between align-items-center">
              <strong>Crop logo for sidebar (4:1)</strong>
              <button
                type="button"
                className="close"
                onClick={closeCropModal}
                disabled={cropping}
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="card-body">
              <p className="text-muted mb-2" style={{ fontSize: 13 }}>
                Drag to position · scroll/slider to zoom · output {LOGO_OUTPUT_WIDTH}×
                {LOGO_OUTPUT_HEIGHT}px
              </p>
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  height: 320,
                  background: "#111",
                  borderRadius: 8,
                  overflow: "hidden"
                }}
              >
                {cropSrc ? (
                  <Cropper
                    image={cropSrc}
                    crop={crop}
                    zoom={zoom}
                    aspect={LOGO_CROP_ASPECT}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                    showGrid
                  />
                ) : null}
              </div>
              <div className="mt-3">
                <label className="form-label mb-1" style={{ fontSize: 12 }}>
                  Zoom
                </label>
                <input
                  type="range"
                  className="form-control-range w-100"
                  min={1}
                  max={3}
                  step={0.05}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  disabled={cropping}
                />
              </div>
            </div>
            <div className="card-footer d-flex justify-content-end" style={{ gap: 10 }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeCropModal}
                disabled={cropping}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={applyCropAndUpload}
                disabled={cropping || !croppedAreaPixels}
              >
                {cropping ? "Applying…" : "Apply crop & upload"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default LogoCreate;
